import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, CalendarClock, Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { CONTACT } from "./config";
import { trackLead } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = ["9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM"];
const INDUSTRIES = ["Manufacturing", "Food & Beverage", "Wholesale", "Packaging", "Construction", "Trades", "Professional Services", "Other"];
const SERVICES = ["Content Production", "Social Media Management", "Paid Advertising", "CRM & AI Systems", "Websites", "Automation", "Not sure yet"];
const today = new Date().toISOString().split("T")[0];

const emptyForm = { full_name: "", email: "", phone: "", business_name: "", industry: "", service: "", message: "", preferred_date: "", preferred_time: "" };

const inputCls = "mt-2 w-full bg-transparent border-b border-[#2E1B16]/20 py-3 text-lg text-[#2E1B16] placeholder:text-[#2E1B16]/30 focus:border-[#38BDF8] focus:outline-none transition-colors";

const Field = ({ label, className = "", ...props }) => (
  <label className={`block ${className}`}>
    <span className="label-eyebrow text-[#2E1B16]/50 text-[0.65rem]">{label}</span>
    <input {...props} className={inputCls} />
  </label>
);

const Select = ({ label, options, className = "", ...props }) => (
  <label className={`block ${className}`}>
    <span className="label-eyebrow text-[#2E1B16]/50 text-[0.65rem]">{label}</span>
    <select {...props} className={inputCls}>
      <option value="">Select</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

const startMode = () => (typeof window !== "undefined" && window.location.hash.includes("book") ? "strategy_call" : "website_enquiry");

const Contact = ({
  pageLabel = "Homepage",
  eyebrow = "Get in touch",
  title = <>Tell us about your <span className="font-accent font-normal normal-case text-[#38BDF8]">business.</span></>,
  subtitle = "You don't need to know exactly what marketing you need. Tell us where the business is now, where you're trying to take it and what's getting in the way. We'll take it from there.",
  sectionClassName = "py-28 md:py-44",
  headingTag: Heading = "h2",
}) => {
  const [mode, setMode] = useState(startMode);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isCall = mode === "strategy_call";

  const submit = async (e) => {
    e.preventDefault();
    if (isCall) {
      if (!form.full_name || !form.email || !form.phone || !form.preferred_date || !form.preferred_time) {
        toast.error("Please add your name, email, phone and a preferred date and time.");
        return;
      }
    } else if (!form.full_name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/lead-intake`, { ...form, form_type: mode, page_submitted_from: pageLabel });
      trackLead(mode);
      setDone(mode);
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  const options = [
    { id: "website_enquiry", label: "Send an enquiry", icon: Mail, note: "Tell us about your business. We'll reply by email." },
    { id: "strategy_call", label: "Book a strategy call", icon: CalendarClock, note: "Pick a time and we'll meet properly." },
  ];

  return (
    <section id="contact" className={sectionClassName} data-testid="contact">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <Heading className="mt-6 font-display uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.95] text-[#2E1B16]">
              {title}
            </Heading>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-7 text-lg text-[#2E1B16]/60 max-w-md leading-relaxed">
              {subtitle}
            </p>
          </Reveal>

          <div className="mt-10 space-y-3">
            {options.map((o) => {
              const active = mode === o.id;
              return (
                <button
                  key={o.id}
                  onClick={() => { setMode(o.id); setDone(null); }}
                  data-testid={`contact-option-${o.id}`}
                  className={`group w-full text-left flex items-center gap-4 rounded-2xl border p-5 transition-all duration-300 ${active ? "border-[#38BDF8] bg-white" : "border-[#2E1B16]/12 bg-[#F2EFE9]/40 hover:border-[#2E1B16]/30"}`}
                >
                  <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${active ? "bg-[#38BDF8] text-[#2E1B16]" : "bg-[#2E1B16] text-[#FAF7F2] group-hover:bg-[#38BDF8] group-hover:text-[#2E1B16]"}`}>
                    <o.icon size={18} />
                  </span>
                  <span className="flex-1">
                    <span className="block font-display text-lg tracking-tight text-[#2E1B16]">{o.label}</span>
                    <span className="block text-sm text-[#2E1B16]/55">{o.note}</span>
                  </span>
                  <ArrowRight size={18} className={`transition-all ${active ? "text-[#38BDF8]" : "text-[#2E1B16]/30 group-hover:text-[#38BDF8] group-hover:translate-x-1"}`} />
                </button>
              );
            })}
          </div>

          <Reveal delay={0.15}>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="mt-8 inline-block font-display text-lg md:text-xl text-[#2E1B16] hover:text-[#38BDF8] transition-colors border-b border-[#2E1B16]/20 hover:border-[#38BDF8]">
              {CONTACT.email}
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-[#2E1B16]/12 bg-white/60 p-7 md:p-9 panel-shadow min-h-[420px] flex flex-col justify-center">
            {done ? (
              <div className="text-center py-8" data-testid="contact-success">
                <span className="mx-auto mb-6 w-16 h-16 rounded-full bg-[#38BDF8]/15 flex items-center justify-center">
                  <Check size={30} className="text-[#38BDF8]" strokeWidth={2.5} />
                </span>
                <h3 className="font-display text-3xl tracking-tight text-[#2E1B16]">
                  {done === "strategy_call" ? "You're booked." : "Thanks, we've received your enquiry."}
                </h3>
                <p className="mt-3 text-lg text-[#2E1B16]/60 max-w-sm mx-auto">
                  {done === "strategy_call" ? "Check your email for your calendar invitation." : "We'll be in touch shortly."}
                </p>
                <button onClick={() => setDone(null)} data-testid="contact-reset" className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#2E1B16]/20 px-6 py-3 font-semibold text-[#2E1B16] hover:border-[#2E1B16]/50 transition-colors">
                  Send another
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-display text-2xl tracking-tight text-[#2E1B16] mb-1" data-testid="contact-form-title">
                  {isCall ? "Book a strategy call" : "Send an enquiry"}
                </h3>
                <p className="text-sm text-[#2E1B16]/55 mb-7">
                  {isCall ? "A few details and a time. We'll confirm your invite by email." : "A few details and we'll reply by email."}
                </p>
                <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-7">
                    <Field label="Full name" placeholder="Your name" value={form.full_name} onChange={set("full_name")} data-testid="contact-full-name" />
                    <Field label="Business name" placeholder="Business name" value={form.business_name} onChange={set("business_name")} data-testid="contact-business" />
                    <Field label="Email" type="email" placeholder="you@company.com" value={form.email} onChange={set("email")} data-testid="contact-email" />
                    <Field label="Phone" type="tel" placeholder="Your phone number" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
                    <Select label="Industry" options={INDUSTRIES} value={form.industry} onChange={set("industry")} data-testid="contact-industry" />
                    <Select label="Service you're after" options={SERVICES} value={form.service} onChange={set("service")} data-testid="contact-service" />

                    {isCall && (
                      <>
                        <Field label="Preferred date" type="date" min={today} value={form.preferred_date} onChange={set("preferred_date")} data-testid="contact-date" />
                        <label className="block">
                          <span className="label-eyebrow text-[#2E1B16]/50 text-[0.65rem]">Preferred time</span>
                          <select value={form.preferred_time} onChange={set("preferred_time")} data-testid="contact-time" className={inputCls}>
                            <option value="">Select a time</option>
                            {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </label>
                      </>
                    )}

                    <label className="block sm:col-span-2">
                      <span className="label-eyebrow text-[#2E1B16]/50 text-[0.65rem]">{isCall ? "Anything to cover? (optional)" : "Message"}</span>
                      <textarea rows={isCall ? 2 : 3} placeholder={isCall ? "A line on what you'd like to talk through" : "What are you looking to sort out?"} value={form.message} onChange={set("message")} data-testid="contact-message" className={`${inputCls} resize-none`} />
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="group inline-flex items-center gap-2 rounded-full bg-[#38BDF8] text-[#2E1B16] font-semibold px-8 py-4 hover:bg-[#2E1B16] hover:text-[#FAF7F2] transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading ? "Sending" : isCall ? "Book my call" : "Send enquiry"}
                    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
